/**
 * Right-side slide-in panel. The "task detail" surface for the Kanban board —
 * opening a card overlays the detail instead of navigating away, so the board
 * stays in place behind it.
 *
 * Mirrors `Modal.tsx`'s chrome (backdrop blur + click-to-close, Esc-to-close,
 * body scroll lock, CSS-variable theming) but anchors to the right edge and
 * animates via `motion` (already a workspace dep) so the enter/exit slide is
 * smooth — AnimatePresence gives us an exit animation that pure CSS can't.
 *
 * `ponytail:` no focus-trap yet — tab order can reach the page behind the
 * overlay. Add one if the drawer starts hosting heavy keyboard entry.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { lockBody, unlockBody } from '../lib/body-lock';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Panel width in px (clamped to viewport). */
  width?: number;
  /** Accessible label (falls back to title text). */
  ariaLabel?: string;
}

export function Drawer({ open, onClose, title, children, width = 640, ariaLabel }: DrawerProps) {
  // Callers pass inline onClose arrows (new identity each render). Keep it in a
  // ref so the Esc/scroll-lock effect depends only on `open` and isn't rebound
  // on every parent render while the drawer is open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    lockBody();
    return () => {
      window.removeEventListener('keydown', onKey);
      // Defer the unlock past the ~200ms exit slide (AnimatePresence keeps the
      // panel mounted while it animates out) so the page behind can't scroll
      // under the animating-out panel. The shared counter makes this race-safe
      // across rapid close/reopen — each close schedules its own decrement.
      setTimeout(unlockBody, 250);
    };
  }, [open]);

  const label = typeof title === 'string' ? title : ariaLabel;

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-40 flex justify-end"
          role="dialog"
          aria-modal="true"
          aria-label={label}
        >
          <motion.div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
          />
          <motion.aside
            className="relative h-full flex flex-col shadow-2xl border-l border-border bg-card text-card-foreground"
            style={{ width: `min(${width}px, 100vw)` }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border shrink-0">
                <h2 className="text-sm font-semibold leading-tight truncate">{title}</h2>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition shrink-0"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto">{children}</div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
